import { useEffect, useMemo } from 'react';
import {
  generatePath,
  useParams,
  useLocation,
  NavLink,
  Outlet,
} from 'react-router-dom';
import { CASP } from '@ui/src/components/transactions-sidepanels/types';
import {
  formatDate,
  IconExpand,
  PermissionDenied,
  PermissionEnum,
  SchemaFilterType,
  SelectItemProps,
  Table,
  useFilters,
  useGetList,
  usePrevious,
  useQueryState,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { CustomerStatus, ExternalAccount, OrganizationType } from '../types';
import { PaymentType } from '../../../store/dictionaries/types';
import {
  columnsItems,
  columnsLabels,
  filtersSchema,
  schema,
} from './variables/list';
import { clearAccountsState } from '../store/slices/external-accounts';
import { useLazyGetCustomersExternalAccountsListQuery } from '../store/services/customers';

import ROUTES from '../../../routes';

import cn from '../customers.module.css';

const formatExternalList = (
  list?: ExternalAccount[],
  paymentTypes?: PaymentType[],
  casp?: CASP[],
  currenciesList?: SelectItemProps[],
): ExternalAccount[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const availablePaymentTypes: string[] = [];

    item?.pspIntegrationPayment?.paymentsTypes?.forEach((type) => {
      if (
        paymentTypes?.find((paymentType) => paymentType.paymentType === type)
      ) {
        availablePaymentTypes.push(
          paymentTypes?.find((paymentType) => paymentType.paymentType === type)
            ?.name as string,
        );
      }
    });

    const currentCurrencies: string[] = [];

    if (item?.currenciesIds?.length) {
      item?.currenciesIds?.forEach((currencyId) => {
        currentCurrencies.push(
          currenciesList?.find((currency) => currency.value === currencyId)
            ?.name as string,
        );
      });
    }

    const render: Record<string, any> = {
      pspPartnerId: <div>{item?.pspPartner?.companyName}</div>,
      accountId: <div>{item?.account?.fullNumber}</div>,
      createdAt: <div>{formatDate(item?.createdAt as string)}</div>,
      caspId: (
        <div>
          {item?.caspId
            ? casp?.find((caspValue) => caspValue?.id === item.caspId)?.name
            : '-'}
        </div>
      ),
      currencyId: (
        <div className={cn.currenciesWrapper}>
          {currentCurrencies?.join(', ') || '-'}
        </div>
      ),
      paymentType: (
        <div className={cn.paymentTypesRow}>
          {availablePaymentTypes?.join(', ') || '-'}
        </div>
      ),
      active: (
        <div className={!item.active ? cn.disabledStatus : ''}>
          {item?.active ? 'Enabled' : 'Disabled'}
        </div>
      ),
      createType: (
        <div>
          {result.createType
            ? `${result.createType.charAt(0)}${result.createType
                .slice(1)
                .toLowerCase()}`
            : ''}
        </div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList as ExternalAccount[];
};

const CustomerExternalAccounts = ({ type }: { type: OrganizationType }) => {
  const { customerId } = useParams();

  const { currenciesList } = useAppSelector((state) => state.root);
  const { baseInformation: corporateBaseInformation } = useAppSelector(
    (state) => state.corporateCustomers.info.general,
  );
  const { baseInformation: individualBaseInformation } = useAppSelector(
    (state) => state.individualCustomers.info.general,
  );
  const {
    externalAccounts,
    pagination,
    pspPartners,
    openPayNotificationVisible,
  } = useAppSelector((state) => state.customersExternalAccounts);
  const { paymentTypes, activePermissions, casp } = useAppSelector(
    (state) => state.customersDictionaries,
  );

  const prevOpenPayNotificationVisible = usePrevious(
    openPayNotificationVisible,
  );

  const [getExternalAccountsList, { isLoading, isFetching }] =
    useLazyGetCustomersExternalAccountsListQuery();

  const dispatch = useAppDispatch();

  const { search } = useLocation();

  const organizationStatus =
    type === OrganizationType.INDIVIDUAL
      ? individualBaseInformation?.status
      : corporateBaseInformation?.status;

  const { filters, onSortChange, onFilterChange } = useQueryState(schema);

  const prevFilters = usePrevious(filters);

  const { onFilter, setPage } = useGetList<ExternalAccount>({
    getList: getExternalAccountsList,
    onSortChange,
    onFilterChange,
    schema,
  });

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const formattedData = useMemo(() => {
    const nextExternalAccounts = externalAccounts?.map((item) => {
      const result = { ...item };

      result.url =
        type === OrganizationType.INDIVIDUAL
          ? `${generatePath(
              ROUTES.CUSTOMERS_INDIVIDUAL_EXTERNAL_ACCOUNTS_ITEM,
              { customerId: customerId || '', accountId: result.id },
            )}${search || ''}`
          : `${generatePath(ROUTES.CUSTOMERS_CORPORATE_EXTERNAL_ACCOUNTS_ITEM, {
              customerId: customerId || '',
              accountId: result.id,
            })}${search || ''}`;

      return result;
    });

    const data = formatExternalList(
      nextExternalAccounts,
      paymentTypes,
      casp,
      currenciesList,
    );

    return data;
  }, [
    externalAccounts,
    paymentTypes,
    filters,
    type,
    customerId,
    search,
    casp,
    currenciesList,
  ]);

  useEffect(() => {
    setValues(
      'pspPartnerId',
      pspPartners?.map((item) => ({
        name: item.companyName,
        value: item.id,
      })) as SelectItemProps[],
    );
  }, [pspPartners, type]);

  useEffect(() => {
    if (
      !externalAccounts &&
      !search &&
      !Object.keys(filters).length &&
      customerId
    ) {
      onFilter({
        organizationId: {
          value: [customerId],
          type: SchemaFilterType.STRING,
        },
        active: {
          value: ['ENABLED'],
          type: SchemaFilterType.STRING,
        },
      });
    }
  }, [filters, search, externalAccounts, customerId]);

  useEffect(() => {
    if (
      !openPayNotificationVisible &&
      customerId &&
      prevOpenPayNotificationVisible
    ) {
      onFilter({
        organizationId: {
          value: [customerId],
          type: SchemaFilterType.STRING,
        },
        active: {
          value: ['ENABLED'],
          type: SchemaFilterType.STRING,
        },
      });
    }
  }, [openPayNotificationVisible, customerId, prevOpenPayNotificationVisible]);

  useEffect(() => {
    if (
      customerId &&
      !externalAccounts &&
      search &&
      filters &&
      prevFilters &&
      Object.keys(prevFilters).length
    ) {
      const filtersValues: any = {
        ...filters,
        organizationId: { value: [customerId], type: SchemaFilterType.STRING },
        active: {
          value: ['ENABLED'],
          type: SchemaFilterType.STRING,
        },
      };

      onFilter(filtersValues);
    }
  }, [filters, search, externalAccounts, prevFilters, customerId]);

  useEffect(
    () => () => {
      dispatch(clearAccountsState());
    },
    [],
  );

  const link =
    type === OrganizationType.INDIVIDUAL
      ? ROUTES.CUSTOMERS_INDIVIDUAL_EXTERNAL_ACCOUNTS_CREATE
      : ROUTES.CUSTOMERS_CORPORATE_EXTERNAL_ACCOUNTS_CREATE;

  let content;

  if (
    !activePermissions?.includes(PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST)
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <>
        {activePermissions?.includes(
          PermissionEnum.PSP_INTEGRATION_ACCOUNT_ADD_ACTION,
        ) &&
          organizationStatus !== CustomerStatus.CLOSED && (
            <div className={cn.externalAddLinkWrapper}>
              <NavLink
                to={`${generatePath(link, {
                  customerId: (customerId as string) || '',
                })}${search || ''}`}
                className={cn.externalAddLink}
              >
                <IconExpand width={16} height={16} />
                Add External Account
              </NavLink>
            </div>
          )}
        <div className={cn.externalAccountsTable}>
          <Table
            columns={columnsItems}
            data={formattedData || []}
            onSort={() => {}}
            sort={{}}
            filters={filters}
            columnsLabels={columnsLabels}
            onFilterChange={(values) => {
              onFilter({
                ...values,
                organizationId: {
                  value: [customerId as string],
                  type: SchemaFilterType.STRING,
                },
              });
            }}
            filtersComponent={filtersComponent}
            handleSubmit={handleSubmit}
            isLoading={isLoading || isFetching}
            pagination={pagination}
            tableBodyClassName={isLoading || isFetching ? cn.tableLoading : ''}
            take="20"
            tableTopClassName={
              Object.keys(filters)?.length ? cn.externalWithPadding : ''
            }
            setPage={(data) => {
              const filterValues = {
                ...filters,
                organizationId: {
                  value: [customerId as string],
                  type: SchemaFilterType.STRING,
                },
              };

              setPage({ ...data, filters: filterValues });
            }}
            linkWithoutSearch
            withVerticalScroll
            withScroll
            clickable
          />
        </div>
        <Outlet />
      </>
    );
  }

  return content;
};

export default CustomerExternalAccounts;
